import { skipToken } from '@reduxjs/toolkit/query';
import { Alert, Spin } from 'antd';
import React from 'react';
import {
  useCreateScopeSectionMutation,
  useDeleteScopeSectionMutation,
  useDeleteScopeSectionRuleMutation,
  useGetScopeQuery,
  useGetScopeSectionRuleOptionsQuery,
  useUpdateScopeMutation,
  usePutScopeSectionRuleMutation,
} from '../../../apis/apiScopes';
import {
  useGetBrandsQuery,
  useGetDepartmentsQuery,
  useGetRegionsQuery,
  useGetSkuClassesQuery,
  useGetStoresQuery,
  useGetStoreTypesQuery,
} from '../../../apis/apiLists';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';
import ScopeEditor from './ScopeEditor';

interface ScopeEditorControllerProps {
  allowArchive?: boolean;
  allowSharing?: boolean;
  detailsContent?: React.ReactNode;
  embedded?: boolean;
  footerContent?: React.ReactNode;
  onEditAttributes?: () => void;
  projectShortName: string;
  readOnly?: boolean;
  scopeId: number;
}

const ScopeEditorController: React.FC<ScopeEditorControllerProps> = ({
  allowArchive = false,
  allowSharing = false,
  detailsContent,
  embedded = false,
  footerContent,
  onEditAttributes,
  projectShortName,
  readOnly = false,
  scopeId,
}) => {
  const selectedTemplate = useTemplateOrCalculationWithValue('Template');
  const {
    data: scope,
    isFetching,
    isError,
  } = useGetScopeQuery(projectShortName ? { projectShortName, scopeId } : skipToken);
  const { data: ruleOptions, isFetching: isFetchingRuleOptions } =
    useGetScopeSectionRuleOptionsQuery();
  const { data: activeBrands, isFetching: isFetchingBrands } = useGetBrandsQuery(
    selectedTemplate.value,
  );
  const { data: activeSkuClasses, isFetching: isFetchingSkuClasses } = useGetSkuClassesQuery(
    selectedTemplate.value,
  );
  const { data: activeRegions, isFetching: isFetchingRegions } = useGetRegionsQuery(
    selectedTemplate.value,
  );
  const { data: activeStoreTypes, isFetching: isFetchingStoreTypes } = useGetStoreTypesQuery(
    selectedTemplate.value,
  );
  const { data: activeStores, isFetching: isFetchingStores } = useGetStoresQuery(
    selectedTemplate.value,
  );
  const { data: activeDepartments, isFetching: isFetchingDepartments } = useGetDepartmentsQuery(
    selectedTemplate.value,
  );
  const [updateScope, updateState] = useUpdateScopeMutation();
  const [createScopeSection, createSectionState] = useCreateScopeSectionMutation();
  const [putScopeSectionRule, putRuleState] = usePutScopeSectionRuleMutation();
  const [deleteScopeSectionRule, deleteRuleState] = useDeleteScopeSectionRuleMutation();
  const [deleteScopeSection, deleteSectionState] = useDeleteScopeSectionMutation();

  if (isFetching && !scope) return <Spin />;
  if (isError || !scope) {
    return <Alert message="The selected Scope could not be loaded." showIcon type="error" />;
  }

  return (
    <ScopeEditor
      activeEntities={{
        brands: activeBrands ?? [],
        departments: activeDepartments?.array ?? [],
        regions: activeRegions?.array ?? [],
        skuClasses: activeSkuClasses ?? [],
        stores: activeStores?.array ?? [],
        storeTypes: activeStoreTypes?.array ?? [],
      }}
      detailsContent={detailsContent}
      embedded={embedded}
      footerContent={footerContent}
      ruleOptions={ruleOptions}
      isLoadingOptions={
        isFetchingRuleOptions ||
        isFetchingBrands ||
        isFetchingSkuClasses ||
        isFetchingRegions ||
        isFetchingStoreTypes ||
        isFetchingStores ||
        isFetchingDepartments
      }
      isSaving={
        updateState.isLoading ||
        createSectionState.isLoading ||
        putRuleState.isLoading ||
        deleteRuleState.isLoading ||
        deleteSectionState.isLoading
      }
      onEditAttributes={onEditAttributes}
      readOnly={readOnly}
      selection={scope}
      onArchiveStateChange={
        allowArchive
          ? isArchived =>
              updateScope({ scopeId, request: { isArchived } })
                .unwrap()
                .then(() => undefined)
          : undefined
      }
      onSharedStateChange={
        allowSharing
          ? isShared =>
              updateScope({ scopeId, request: { isShared } })
                .unwrap()
                .then(() => undefined)
          : undefined
      }
      onCreateSection={request => createScopeSection({ scopeId, request }).unwrap()}
      onDeleteRule={(scopeSectionId, scopeSectionRuleTypeId) =>
        deleteScopeSectionRule({ scopeId, scopeSectionId, scopeSectionRuleTypeId })
          .unwrap()
          .then(() => undefined)
      }
      onDeleteSection={scopeSectionId =>
        deleteScopeSection({ scopeId, scopeSectionId })
          .unwrap()
          .then(() => undefined)
      }
      onUpsertRule={(scopeSectionId, request) =>
        putScopeSectionRule({
          scopeId,
          scopeSectionId,
          ruleType: request.scopeSectionRuleTypeId,
          request: {
            scopeSectionRuleModeId: request.scopeSectionRuleModeId,
            ids: request.ids,
          },
        })
          .unwrap()
          .then(() => undefined)
      }
    />
  );
};

export default ScopeEditorController;
